/**
 * Fabric overlay helpers for PDF Studio.
 * Mobile Safari/Chrome often fail to clear Fabric's upper canvas (selection
 * chrome) between frames — especially with retina scaling or a CSS transform
 * on a parent — leaving a trailing mesh of blue handles.
 */

import type { fabric as FabricNS } from 'fabric';

export function isCoarsePointer(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

type OverlayCanvas = FabricNS.Canvas & {
  contextTop?: CanvasRenderingContext2D;
};

/** Clear the full backing store, ignoring any leftover retina transform. */
export function patchFabricOverlayClear(canvas: FabricNS.Canvas): void {
  canvas.clearContext = (ctx: CanvasRenderingContext2D) => {
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
    return canvas;
  };
}

export function overlayTopContext(
  canvas: FabricNS.Canvas
): CanvasRenderingContext2D | undefined {
  return (canvas as OverlayCanvas).contextTop;
}

export function stylePdfAnnotationControls(
  obj: FabricNS.Object,
  coarse = isCoarsePointer()
): void {
  obj.set({
    borderColor: '#2b7cff',
    cornerColor: '#2b7cff',
    cornerStrokeColor: '#2b7cff',
    cornerStyle: 'circle',
    transparentCorners: false,
    cornerSize: coarse ? 14 : 10,
    borderScaleFactor: 1.5,
    objectCaching: true,
  });
}

/** Multi-select: only the group box, never per-object handles. */
export function suppressChildSelectionChrome(active: FabricNS.Object | null | undefined): void {
  if (!active || active.type !== 'activeSelection') return;
  const group = active as FabricNS.ActiveSelection;
  group.getObjects().forEach((child) => {
    child.set({ hasControls: false, hasBorders: false });
  });
  stylePdfAnnotationControls(group);
}
